import { useState } from "react";

// Shared modal used by all export components
export function ExportModal({ title, filename, content, onClose, extraAction }) {
  const [text, setText] = useState(content);

  const download = () => {
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:2000 }}>
      <div style={{ background:"#fff", borderRadius:12, padding:24, width:"min(680px,95vw)", maxHeight:"85vh", display:"flex", flexDirection:"column", boxShadow:"0 8px 32px rgba(0,0,0,0.2)" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
          <h3 style={{ fontSize:16, fontWeight:700, color:"#0F172A", margin:0 }}>{title}</h3>
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#64748b" }}>✕</button>
        </div>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          style={{ flex:1, minHeight:320, padding:12, fontSize:12, fontFamily:"monospace", border:"1px solid #E2E8F0", borderRadius:8, resize:"vertical", lineHeight:1.6 }}
        />
        <div style={{ display:"flex", gap:8, marginTop:14, justifyContent:"flex-end" }}>
          <button onClick={onClose} style={{ padding:"8px 16px", background:"#f1f5f9", color:"#334155", border:"none", borderRadius:8, fontSize:13, cursor:"pointer" }}>Cancel</button>
          <button onClick={download} style={{ padding:"8px 16px", background:"#1E3A5F", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer" }}>⬇ Download .txt</button>
          {extraAction && <button onClick={() => extraAction.onClick(onClose)} style={{ padding:"8px 16px", background:"#7C3AED", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer" }}>{extraAction.label}</button>}
        </div>
      </div>
    </div>
  );
}

// ── Buffer CSV ────────────────────────────────────────────────────────────────
export function ExportBufferCSV({ queue }) {
  const platforms = [
    { label: 'LinkedIn',  icon: '💼', bg: '#0A66C2' },
    { label: 'Facebook',  icon: '📘', bg: '#1877F2' },
    { label: 'Tiktok',    icon: '🎵', bg: '#010101' },
  ];
  return (
    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
      {platforms.map(({ label, icon, bg }) => (
        <button
          key={label}
          onClick={() => {
            import('../utils/exportBufferCsv.js').then(m => {
              m.exportItemsToBufferCsv(queue, label, new Date().toISOString().slice(0,10), null, 10);
            });
          }}
          style={{ padding:'7px 13px', fontSize:12, fontWeight:600, background:bg, color:'#fff', border:'none', borderRadius:7, cursor:'pointer' }}
        >
          {icon} Buffer · {label}
        </button>
      ))}
    </div>
  );
}

// ── Pictory Scripts ───────────────────────────────────────────────────────────
export function ExportPictoryScripts({ queue }) {
  const [open, setOpen] = useState(false);

  const getContent = () => {
    const items = queue.filter(i => ["TikTok","YouTube Shorts","Facebook","Instagram","Facebook Reels","Instagram Reels"].includes(i.platform));
    if (!items.length) return "No video platform posts in queue.";
    const lines = ["STRATEGIC HONESTY — PICTORY VIDEO SCRIPTS", "Generated: " + new Date().toLocaleDateString(), "", "═".repeat(60), ""];
    items.forEach((item, i) => {
      lines.push(`PROJECT ${i+1}: ${item.platform.toUpperCase()} — ${item.title}`);
      lines.push("─".repeat(50));
      lines.push(item.content);
      lines.push("");
    });
    return lines.join("\n");
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{ padding:"7px 13px", fontSize:12, fontWeight:600, background:"#FF0000", color:"#fff", border:"none", borderRadius:7, cursor:"pointer" }}
      >
        🎬 Pictory Scripts
      </button>
      {open && (
        <ExportModal
          title="Pictory Scripts"
          filename={`SH_PictoryScripts_${new Date().toISOString().split("T")[0]}.txt`}
          content={getContent()}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// ── Image Prompts ─────────────────────────────────────────────────────────────
export function ExportImagePrompts({ queue }) {
  const [open, setOpen] = useState(false);

  const imageItems = queue.filter(i => i.imageprompt);

  const getContent = () => {
    if (!imageItems.length) return "No image prompts available.";
    const lines = ["STRATEGIC HONESTY — IMAGE & THUMBNAIL PROMPTS", "Generated: " + new Date().toLocaleDateString(), "Paste into Midjourney, DALL-E, Ideogram, or Canva AI", "", "═".repeat(60), ""];
    imageItems.forEach((item, i) => {
      lines.push(`PROMPT ${i+1}: ${item.title} (${item.platform})`);
      lines.push("─".repeat(50));
      lines.push(item.imageprompt);
      lines.push("");
    });
    return lines.join("\n");
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{ padding:"7px 13px", fontSize:12, fontWeight:600, background:"#E1306C", color:"#fff", border:"none", borderRadius:7, cursor:"pointer" }}
      >
        🎨 Image Prompts
      </button>
      {open && (
        <ExportModal
          title="Image Prompts"
          filename={`SH_ImagePrompts_${new Date().toISOString().split("T")[0]}.txt`}
          content={getContent()}
          onClose={() => setOpen(false)}
          extraAction={imageItems.length > 0 ? {
            label: `🖼️ Send ${imageItems.length} to Make Image`,
            onClick: async (onClose) => {
              const { sendToMediaStudio } = await import('../services/mediaStudioService');
              let sent = 0;
              for (const item of imageItems) {
                try {
                  await sendToMediaStudio({
                    schedulerPostId: item.id,
                    title:       item.title,
                    platform:    item.platform,
                    contentType: item.contentType || 'image_post',
                    sourceText:  item.content || '',
                    imagePrompt: item.imageprompt || '',
                    videoPrompt: '',
                    sceneData:   {},
                    status:      'pending',
                  });
                  sent++;
                } catch(e) { console.warn('Failed:', item.title, e.message); }
              }
              alert('✅ Sent ' + sent + ' image prompt' + (sent!==1?'s':'') + ' to Make Image!');
              onClose();
            }
          } : null}
        />
      )}
    </>
  );
}

// ── Video Prompts ────────────────────────────────────────────────────────────
export function ExportVideoPrompts({ queue }) {
  const [open, setOpen] = useState(false);

  const isVideoItem = (item) => {
    const p = (item.platform||'').toLowerCase();
    const c = (item.contentType||item.content_type||'').toLowerCase();
    return p.includes('tiktok')||p.includes('youtube')||p.includes('reel')||
           c.includes('video')||c.includes('reel')||c.includes('short');
  };

  const getContent = () => {
    const items = queue.filter(i => isVideoItem(i));
    if (!items.length) return "No video prompts available.";
    const lines = ["STRATEGIC HONESTY — VIDEO PROMPTS", "Generated: " + new Date().toLocaleDateString(), "Paste into Make Video in Media Studio", "", "═".repeat(60), ""];
    items.forEach((item, i) => {
      lines.push(`PROMPT ${i+1}: ${item.title} (${item.platform})`);
      lines.push("─".repeat(50));
      lines.push(item.videoPrompt || item.content || '(no video prompt)');
      lines.push("");
    });
    return lines.join("\n");
  };

  const videoItems = queue.filter(i => isVideoItem(i));

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{ padding:"7px 13px", fontSize:12, fontWeight:600, background:"#7C3AED", color:"#fff", border:"none", borderRadius:7, cursor:"pointer" }}
      >
        🎬 Video Prompts
      </button>
      {open && (
        <ExportModal
          title="Video Prompts"
          filename={`SH_VideoPrompts_${new Date().toISOString().split("T")[0]}.txt`}
          content={getContent()}
          onClose={() => setOpen(false)}
          extraAction={videoItems.length > 0 ? {
            label: `🎬 Send ${videoItems.length} to Make Video`,
            onClick: async (onClose) => {
              const { sendToMediaStudio } = await import('../services/mediaStudioService');
              let sent = 0;
              for (const item of videoItems) {
                try {
                  await sendToMediaStudio({
                    schedulerPostId: item.id,
                    title:       item.title,
                    platform:    item.platform,
                    contentType: 'short_video',
                    sourceText:  item.content || '',
                    imagePrompt: '',
                    videoPrompt: item.videoPrompt || item.content || '',
                    sceneData:   {},
                    status:      'pending',
                  });
                  sent++;
                } catch(e) { console.warn('Failed:', item.title, e.message); }
              }
              alert('✅ Sent ' + sent + ' video prompt' + (sent!==1?'s':'') + ' to Make Video!');
              onClose();
            }
          } : null}
        />
      )}
    </>
  );
}

// ── Creative Brief ────────────────────────────────────────────────────────────
export function ExportCreativeBrief({ queue }) {
  const [open, setOpen] = useState(false);

  const getContent = () => {
    if (!queue.length) return "No approved posts in queue.";
    const lines = ["STRATEGIC HONESTY — MASTER CREATIVE BRIEF", "Generated: " + new Date().toLocaleDateString(), "Brand: StrategicHonesty.com | Author: Gopu Shrestha", "Philosophy: Be Good. Do Good. Do Well.", "", "═".repeat(60), ""];
    queue.forEach((item, i) => {
      lines.push(`${i+1}. ${item.platform.toUpperCase()} — ${item.title}`);
      lines.push("─".repeat(50));
      lines.push("APPROVED COPY:");
      lines.push(item.content);
      lines.push("");
      if (item.imageprompt) {
        lines.push("IMAGE/THUMBNAIL PROMPT:");
        lines.push(item.imageprompt);
        lines.push("");
      }
      lines.push("VISUAL DIRECTION: Dark navy background, gold accent typography, professional but human.");
      lines.push("BRAND SIGN-OFF: Be Good. Do Good. Do Well. — Gopu Shrestha");
      lines.push("");
      lines.push("═".repeat(60));
      lines.push("");
    });
    return lines.join("\n");
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{ padding:"7px 13px", fontSize:12, fontWeight:600, background:"#C9A84C", color:"#0F172A", border:"none", borderRadius:7, cursor:"pointer" }}
      >
        📄 Creative Brief
      </button>
      {open && (
        <ExportModal
          title="Creative Brief"
          filename={`SH_CreativeBrief_${new Date().toISOString().split("T")[0]}.txt`}
          content={getContent()}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
